import { useState, useEffect } from 'react'
import type { Panel, PanelDictionaryEntry } from '@alpha-edge/shared'
import {
  Modal,
  FormField,
  btnPrimary,
  btnSecondary,
  btnDanger,
  colors,
  input,
  ConfirmDialog,
} from '../../components/ui'
import { useModal } from '../../hooks/use-modal'
import { api, ApiError } from '../../services/api'

interface DictionaryForm {
  panelId: string
  interpretationsJson: string
  rejectionsJson: string
}

const defaults: DictionaryForm = {
  panelId: '',
  interpretationsJson: '[]',
  rejectionsJson: '[]',
}

interface PanelDictionaryEditModalProps {
  readonly entryId: string | null
  readonly isOpen: boolean
  readonly isNew: boolean
  readonly onClose: () => void
  readonly onSaved: (entry: PanelDictionaryEntry) => void
  readonly onDeleted?: (entryId: string) => void
}

function entryToForm(e: PanelDictionaryEntry): DictionaryForm {
  return {
    panelId: e.panelId ?? '',
    interpretationsJson: JSON.stringify(e.interpretationTemplates, null, 2),
    rejectionsJson: JSON.stringify(e.rejectionReasons, null, 2),
  }
}

export function PanelDictionaryEditModal({
  entryId,
  isOpen,
  isNew,
  onClose,
  onSaved,
  onDeleted,
}: PanelDictionaryEditModalProps) {
  const [form, setForm] = useState<DictionaryForm>(defaults)
  const [panels, setPanels] = useState<readonly Panel[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const deleteModal = useModal()
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    let cancelled = false
    api
      .get<readonly Panel[]>('/panels?isActive=true&limit=100')
      .then((res) => {
        if (cancelled) return
        if (res.data) setPanels(res.data)
      })
      .catch(() => {
        // non-fatal
      })
    return () => {
      cancelled = true
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    if (isNew) {
      setForm(defaults)
      setError(null)
      return
    }
    if (!entryId) return
    let cancelled = false
    setLoading(true)
    setError(null)
    api
      .get<PanelDictionaryEntry>(`/panel-dictionary/${entryId}`)
      .then((res) => {
        if (cancelled) return
        if (res.data) setForm(entryToForm(res.data))
        setLoading(false)
      })
      .catch((err) => {
        if (cancelled) return
        setError(err instanceof ApiError ? err.message : 'Failed to load entry')
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [entryId, isOpen, isNew])

  const update = <K extends keyof DictionaryForm>(key: K, value: DictionaryForm[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }))

  async function handleSave() {
    setSaving(true)
    setError(null)
    try {
      let interpretationTemplates: unknown
      let rejectionReasons: unknown
      try {
        interpretationTemplates = JSON.parse(form.interpretationsJson || '[]')
        if (!Array.isArray(interpretationTemplates)) throw new Error('interpretations must be a JSON array')
        rejectionReasons = JSON.parse(form.rejectionsJson || '[]')
        if (!Array.isArray(rejectionReasons)) throw new Error('rejections must be a JSON array')
      } catch (err) {
        setError(`JSON invalid: ${err instanceof Error ? err.message : String(err)}`)
        setSaving(false)
        return
      }

      if (isNew) {
        const payload: Record<string, unknown> = {
          panelId: form.panelId || null,
          interpretationTemplates,
          rejectionReasons,
        }
        const res = await api.post<PanelDictionaryEntry>('/panel-dictionary', payload)
        if (!res.data) throw new Error('No data returned')
        onSaved(res.data)
      } else {
        const payload: Record<string, unknown> = {
          interpretationTemplates,
          rejectionReasons,
        }
        const res = await api.put<PanelDictionaryEntry>(`/panel-dictionary/${entryId}`, payload)
        if (!res.data) throw new Error('No data returned')
        onSaved(res.data)
      }
      onClose()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete() {
    if (!entryId) return
    setDeleting(true)
    try {
      await api.delete(`/panel-dictionary/${entryId}`)
      onDeleted?.(entryId)
      deleteModal.close()
      onClose()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to delete')
    } finally {
      setDeleting(false)
    }
  }

  const panelLabel = form.panelId ? (panels.find((p) => p.id === form.panelId)?.name ?? form.panelId) : 'Org default'
  const title = isNew ? 'New Dictionary Entry' : `Dictionary: ${panelLabel}`

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} title={title} width="780px">
        {loading ? (
          <div style={{ color: colors.textMuted, padding: '1rem 0' }}>Loading...</div>
        ) : (
          <div>
            <FormField name="panelId" label="Scope">
              {isNew ? (
                <select style={input} value={form.panelId} onChange={(e) => update('panelId', e.target.value)}>
                  <option value="">Organization default (applies to all panels)</option>
                  {panels.map((p) => (
                    <option key={p.id} value={p.id}>
                      Panel: {p.code} - {p.name}
                    </option>
                  ))}
                </select>
              ) : (
                <input style={input} value={panelLabel} disabled />
              )}
            </FormField>

            <div style={{ marginTop: '0.75rem' }}>
              <FormField name="interpretationsJson" label="Interpretation Templates (JSON)">
                <textarea
                  style={{
                    ...input,
                    minHeight: '140px',
                    resize: 'vertical',
                    fontFamily: 'monospace',
                    fontSize: '0.8125rem',
                  }}
                  value={form.interpretationsJson}
                  onChange={(e) => update('interpretationsJson', e.target.value)}
                  placeholder='[{"key":"normal","label":"Within range","body":"All values within reference range."}]'
                />
              </FormField>
              <p style={{ margin: '0.25rem 0 0', fontSize: '0.8125rem', color: colors.textMuted }}>
                Snippets inserted into result reports. Each entry needs <code>key</code>, <code>label</code>, and{' '}
                <code>body</code>.
              </p>
            </div>

            <div style={{ marginTop: '0.75rem' }}>
              <FormField name="rejectionsJson" label="Rejection Reasons (JSON)">
                <textarea
                  style={{
                    ...input,
                    minHeight: '140px',
                    resize: 'vertical',
                    fontFamily: 'monospace',
                    fontSize: '0.8125rem',
                  }}
                  value={form.rejectionsJson}
                  onChange={(e) => update('rejectionsJson', e.target.value)}
                  placeholder='[{"code":"HEMOLYSIS","label":"Hemolyzed specimen","requiresNote":true}]'
                />
              </FormField>
              <p style={{ margin: '0.25rem 0 0', fontSize: '0.8125rem', color: colors.textMuted }}>
                Controlled vocabulary shown when rejecting a result. Each entry needs <code>code</code> and{' '}
                <code>label</code>; <code>requiresNote</code> defaults to false.
              </p>
            </div>

            {error && (
              <div
                style={{
                  marginTop: '0.75rem',
                  color: colors.danger,
                  padding: '0.5rem 0.75rem',
                  background: colors.bgError,
                  border: `1px solid ${colors.borderError}`,
                  borderRadius: '3px',
                  fontSize: '0.875rem',
                }}
              >
                {error}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.25rem' }}>
              {!isNew && onDeleted && (
                <button
                  type="button"
                  onClick={deleteModal.open}
                  style={{ ...btnDanger, marginRight: 'auto' }}
                  disabled={saving || deleting}
                >
                  Delete
                </button>
              )}
              <button type="button" onClick={onClose} style={btnSecondary} disabled={saving}>
                Cancel
              </button>
              <button type="button" onClick={handleSave} style={btnPrimary} disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        isOpen={deleteModal.isOpen}
        onClose={deleteModal.close}
        onConfirm={handleDelete}
        title="Delete Dictionary Entry"
        message={`Delete the dictionary entry for ${panelLabel}? This action cannot be undone.`}
        confirmLabel="Delete"
        loading={deleting}
      />
    </>
  )
}
